// Offline latitude/longitude → district/state lookup.
//
// Loads a simplified India district-boundary GeoJSON (public/geo/india-districts.geojson,
// GADM ADM2, ~2.5 MB) once, on first use, and resolves points via ray-casting
// point-in-polygon. No external geocoding service — deterministic and offline.
//
// NOTE ON COVERAGE: the bundled dataset is an older ADM2 set. It covers the
// current field regions (UP, Bihar, Jharkhand, Chhattisgarh, etc.) but predates
// some newer states/districts (e.g. Telangana is absent — those points resolve
// to the parent state's old district). Swap the asset to update coverage.

export interface DistrictHit {
  state: string;
  district: string;
}

type Ring = number[][]; // [ [lng,lat], ... ]
interface Feat {
  state: string;
  district: string;
  polys: Ring[][]; // list of polygons; each polygon = [outerRing, ...holes]
  bbox: [number, number, number, number]; // minLng, minLat, maxLng, maxLat
}

let featuresPromise: Promise<Feat[]> | null = null;
const cache = new Map<string, DistrictHit | null>();

function bboxOf(polys: Ring[][]): [number, number, number, number] {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const poly of polys) {
    for (const [x, y] of poly[0]) {
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }
  return [minX, minY, maxX, maxY];
}

async function loadFeatures(): Promise<Feat[]> {
  if (!featuresPromise) {
    featuresPromise = (async () => {
      const res = await fetch(`${import.meta.env.BASE_URL}geo/india-districts.geojson`);
      if (!res.ok) throw new Error(`Failed to load district boundaries (${res.status})`);
      const gj = await res.json();
      return (gj.features as GeoJsonFeature[]).map((f): Feat => {
        const g = f.geometry;
        const polys: Ring[][] =
          g.type === 'MultiPolygon' ? (g.coordinates as Ring[][]) : [g.coordinates as Ring[]];
        return {
          state: f.properties.state,
          district: f.properties.district,
          polys,
          bbox: bboxOf(polys),
        };
      });
    })();
  }
  return featuresPromise;
}

interface GeoJsonFeature {
  properties: { state: string; district: string };
  geometry:
    | { type: 'Polygon'; coordinates: Ring[] }
    | { type: 'MultiPolygon'; coordinates: Ring[][] };
}

// Ray-casting: is point (x,y) inside the ring (even-odd rule)?
function pointInRing(x: number, y: number, ring: Ring): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0], yi = ring[i][1];
    const xj = ring[j][0], yj = ring[j][1];
    const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

// Inside a polygon = inside its outer ring and outside all its holes.
function pointInPolygon(x: number, y: number, poly: Ring[]): boolean {
  if (!pointInRing(x, y, poly[0])) return false;
  for (let h = 1; h < poly.length; h++) {
    if (pointInRing(x, y, poly[h])) return false;
  }
  return true;
}

/**
 * Resolve a coordinate to its district/state, or null if outside all boundaries
 * (bad GPS, coverage gap, or a point in the ocean). Results are memoised on a
 * ~1 km grid (coords rounded to 2 decimals) so a batch of nearby sessions only
 * pays the polygon scan once.
 */
export async function lookupDistrict(lat: number, lng: number): Promise<DistrictHit | null> {
  if (
    lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng) ||
    (lat === 0 && lng === 0)
  ) {
    return null;
  }
  const key = `${lat.toFixed(2)},${lng.toFixed(2)}`;
  const cached = cache.get(key);
  if (cached !== undefined) return cached;

  const feats = await loadFeatures();
  let hit: DistrictHit | null = null;
  for (const f of feats) {
    const [minX, minY, maxX, maxY] = f.bbox;
    if (lng < minX || lng > maxX || lat < minY || lat > maxY) continue;
    for (const poly of f.polys) {
      if (pointInPolygon(lng, lat, poly)) {
        hit = { state: f.state, district: f.district };
        break;
      }
    }
    if (hit) break;
  }
  cache.set(key, hit);
  return hit;
}
